import {
  type GET_NETWORK_CONFIG_OPTION,
  type NETWORK_CONFIG_OPTION
} from './type';
import { getCommonHeaders, parseJSON } from './helper';
import {
  DELETE,
  PATCH,
  POST,
  PUT,
  GET,
  OPS_PORTAL,
  UNAUTHORIZED_MESSAGE,
  CREATE_TICKET_API_URL
} from './constants';
import { isBrowser } from '../browser';
import { HTTP_STATUS_CODE, DFG_STATUS_CODES } from '@dfg/brahma-types';
import { redirectTo } from '../document';
import { apiErrorManager } from '../apiErrorTracker';
import {
  logAutomatedError,
  extractResponseDiagnostics
} from './automatedErrorLogger';

export const request = async (url: string, options: any = {}) => {
  const headers = options?.headers || {};
  const traceId = headers['x-request-id'];
  const payload = options?.body;

  return await new Promise<any>((resolve, reject) => {
    if (!isBrowser()) {
      if (url.startsWith('/api')) {
        url = url.replace('/api', process.env.NEXT_PUBLIC_BFF_URL ?? '');
      } else if (url.startsWith('/kapi')) {
        url = url.replace('/kapi', process.env.NEXT_PUBLIC_KUBER_URL ?? '');
      } else if (url.startsWith('/wf-api')) {
        url = url.replace(
          '/wf-api',
          process.env.NEXT_PUBLIC_WORKFLOW_TOOLS_URL ?? ''
        );
      } else if (url.startsWith('/sc-api')) {
        url = url.replace(
          '/sc-api',
          process.env.NEXT_PUBLIC_SVAYAMCHALAK_URL ?? ''
        );
      }
    }

    fetch(url, { ...options })
      .then(async (response) => {
        const pageUrl =
          typeof window !== 'undefined'
            ? window.location.href
            : headers?.referer ?? '';
        const parsedResponse = await parseJSON(response);

        // Handle redirect responses (payment done or VR cancelled)
        if (
          (response as any)?.status === HTTP_STATUS_CODE.OK &&
          (parsedResponse?.code === DFG_STATUS_CODES.PAYMENT_DONE ||
            parsedResponse?.code === DFG_STATUS_CODES.VR_CANCELLED) &&
          isBrowser()
        ) {
          // Perform client-side redirect to provided URL or fallback to root ('/') which redirects to portal's entry point
          window.location.href = parsedResponse?.data?.redirectUrl || '/';

          // return without resolving the promise as user is redirected to the new page
          return;
        }

        if (response.ok) {
          resolve(parsedResponse);
          return;
        }

        // redirect & reject with proper Error message
        if (
          (response as any)?.status === HTTP_STATUS_CODE.UNAUTHORIZED &&
          process.env.NEXT_PUBLIC_LOGIN_URL &&
          process.env.NEXT_PUBLIC_PORTAL === OPS_PORTAL
        ) {
          redirectTo(process.env.NEXT_PUBLIC_LOGIN_URL, '_blank');
          // TO DO: [Tech Debt] DT-9454 remove eslint-disable-next-line, when rejecting a promise
          // eslint-disable-next-line prefer-promise-reject-errors
          reject({
            ...parsedResponse,
            error: {
              ...(parsedResponse?.error ?? {}),
              dfgMessage: UNAUTHORIZED_MESSAGE
            }
          });
        }
        if (parsedResponse?.error) {
          apiErrorManager.trackFailedApi(url, parsedResponse, traceId, payload);

          if (url !== CREATE_TICKET_API_URL) {
            const description =
              parsedResponse.error?.dfgMessage ??
              parsedResponse.error?.message ??
              '';
            const status =
              parsedResponse.error?.httpStatusCode ??
              parsedResponse.error?.statusCode ??
              '';

            void logAutomatedError({
              traceId: traceId ?? null,
              documentId: null,
              description,
              error: description,
              status,
              url: pageUrl,
              uniqueErrorId: `${url.split('?')[0]}-${status}`
            });
          }
        } else {
          apiErrorManager.trackFailedApi(url, parsedResponse, traceId, payload);

          // Capture actual HTTP status and error message from non-DFG responses
          // (e.g., API Gateway 502/403/429 which return { "message": "..." } without "error" field)
          const actualStatus = (response as any)?.status ?? 500;
          const actualDescription =
            parsedResponse?.message ??
            parsedResponse?.Message ??
            (typeof parsedResponse === 'string'
              ? parsedResponse.substring(0, 200)
              : 'Internal Server Error');
          // Extract infra headers (x-amzn-requestid, x-amzn-trace-id) for CloudWatch cross-referencing
          const diagnostics = extractResponseDiagnostics(response);

          void logAutomatedError({
            traceId: traceId ?? null,
            documentId: null,
            description: diagnostics
              ? `[HTTP ${actualStatus}] ${actualDescription} | ${diagnostics}`
              : `[HTTP ${actualStatus}] ${actualDescription}`,
            error: actualDescription,
            status: actualStatus,
            url: pageUrl,
            uniqueErrorId: `${url.split('?')[0]}-${actualStatus}`
          });
        }
        reject(parsedResponse);
      })
      .catch((error) => {
        apiErrorManager.trackFailedApi(url, error, traceId, payload);

        // redirect & reject with proper Error message
        if (
          error?.error?.httpStatusCode === HTTP_STATUS_CODE.UNAUTHORIZED &&
          process.env.NEXT_PUBLIC_LOGIN_URL
        ) {
          redirectTo(process.env.NEXT_PUBLIC_LOGIN_URL, '_blank');
          // TO DO: [Tech Debt] DT-9454 remove eslint-disable-next-line, when rejecting a promise
          // eslint-disable-next-line prefer-promise-reject-errors
          reject({
            ...error,
            error: {
              ...(error?.error ?? {}),
              dfgMessage: UNAUTHORIZED_MESSAGE
            }
          });
        }
        reject(error);
      });
  });
};

export const deleteReq = async (
  url: string,
  options: NETWORK_CONFIG_OPTION
) => {
  return await request(url, {
    ...options,
    headers: { ...getCommonHeaders(), ...options.headers },
    method: DELETE,
    credentials: 'include'
  });
};

export const get = async (
  url: string,
  options: GET_NETWORK_CONFIG_OPTION = {}
) => {
  return await request(url, {
    ...options,
    headers: { ...getCommonHeaders(), ...options.headers },
    method: GET,
    credentials: 'include'
  });
};

export const patch = async (url: string, options: NETWORK_CONFIG_OPTION) => {
  return await request(url, {
    ...options,
    headers: { ...getCommonHeaders(), ...options.headers },
    method: PATCH,
    credentials: 'include'
  });
};

export const post = async (url: string, options: NETWORK_CONFIG_OPTION) => {
  return await request(url, {
    ...options,
    headers: { ...getCommonHeaders(), ...options.headers },
    method: POST,
    credentials: 'include'
  });
};

export const put = async (url: string, options: NETWORK_CONFIG_OPTION) => {
  return await request(url, {
    ...options,
    headers: { ...getCommonHeaders(), ...options.headers },
    method: PUT,
    credentials: 'include'
  });
};
